import React, { useCallback, useEffect, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import {
	Backdrop,
	Box,
	Card,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
	IconButton,
	Paper,
} from '@mui/material'
import { getFromStorage } from '../../utils/storage'
import axios from 'axios'
import { DOMAIN, api } from '../../utils/api'
import { handleError } from '../../utils/error'
import { useNavigate, useParams } from 'react-router-dom'
import { currentUserActions, updateScheduleFormActions } from '../../redux/store'
import { useDispatch, useSelector } from 'react-redux'
import { mainGreen } from '../../utils/color'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import OrderInfoCard from '../../components/OrderInfoCard'
import OrderUserAvatar from '../../components/OrderUserAvatar'
import UpdateScheduleOrderForm2 from '../../components/UpdateScheduleOrderForm2'
import ExtendedIconButton from '../../components/button-icons/ExtendedIconButton'
import { orderPanelStyles } from '../../components/common/orderPanelStyles'

export default function ScheduleOrderByStore() {
	const { showUpdateScheduleForm } = useSelector((state) => state.updateScheduleForm)
	const { currentUser } = useSelector((state) => state.currentUser)
	const dispatch = useDispatch()
	const { storeId } = useParams()
	const mediaMd = useMediaQuery('(min-width:768px)')
	const [scheduleOrders, setScheduleOrders] = useState()
	const [nearestScheduleOrders, setNearestScheduleOrders] = useState()
	const [loadingSchedule, setLoadingSchedule] = useState(true)

	const token = getFromStorage('current-user')?.accessToken
	const navigate = useNavigate()

	const [currentImage, setCurrentImage] = useState(null)
	const [isViewerOpen, setIsViewerOpen] = useState(false)
	const [expandedOrders, setExpandedOrders] = useState({})

	const closeImageViewer = () => {
		setCurrentImage(null)
		setIsViewerOpen(false)
	}
	const getNearestScheduleOrders = useCallback(
		async () => {
			try {
				const response = await axios({
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
					},
					url: `${DOMAIN}/user/nearest-schedule/store/${storeId}`,
					withCredentials: true,
				})
				setNearestScheduleOrders(response.data)
				setLoadingSchedule(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, storeId, dispatch, navigate],
	)

	const getScheduleOrders = useCallback(
		async () => {
			try {
				const response = await axios({
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
					},
					url: `${DOMAIN}/user/schedule/store/${storeId}`,
					withCredentials: true,
				})
				setScheduleOrders(response.data)
				setLoadingSchedule(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, storeId, dispatch, navigate],
	)

	useEffect(() => {
		const fetchData = async () => {
			await Promise.all([getNearestScheduleOrders(), getScheduleOrders()])
		}
		fetchData()
	}, [getNearestScheduleOrders, getScheduleOrders])

	const deleteOrder = async (orderId) => {
		try {
			console.log('Deleting order:', orderId)
			if (window.confirm('Bạn có chắc chắn muốn xóa đơn hàng này?')) {
				const response = await axios({
					url: api.DELETE_SCHEDULE,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					data: { orderId },
					method: 'DELETE',
					withCredentials: true,
				})
				await getScheduleOrders()
				if (response.status === 200) {
					alert(response.data.message)
				}
			}
		} catch (error) {
			console.error('Error deleting order:', error)
		}
	}

	const toggleExpand = (orderId) => {
		setExpandedOrders((prev) => ({
			...prev,
			[orderId]: !prev[orderId],
		}))
	}

	const renderOrderCard = (order) => {
		if (!order) return null
		const { createOrderData, timer } = order
		const { creator } = createOrderData

		const showExpandIcon = createOrderData.source || createOrderData.code
		const isExpanded = expandedOrders[order._id]

		return (
			<Paper sx={orderPanelStyles.orderCard}>
				{/* Order Header */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
					<OrderUserAvatar url={creator?.photo || 'path-to-default-avatar.jpg'} />
					<Typography sx={{ color: 'green' }}>
						{creator?.name || 'Unknown'} tạo đơn đặt trước lúc {timer.hours}:{timer.minutes} - {timer.date}/
						{timer.month}/{timer.year}
					</Typography>
				</Box>

				{/* Date Tag */}
				<Box sx={{ mb: 2 }}>
					<Typography
						sx={{
							display: 'inline-block',
							bgcolor: '#4caf50',
							color: 'white',
							px: 1,
							py: 0.5,
							borderRadius: 1,
							fontSize: '0.875rem',
						}}>
						{timer.date}/{timer.month}
					</Typography>
				</Box>

				<OrderInfoCard orderData={createOrderData} isExpanded={isExpanded} />

				{/* Action Buttons */}
				<Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
					<ContentCutIcon
						onClick={(e) => {
							e.stopPropagation()
							dispatch(updateScheduleFormActions.showUpdateScheduleForm())
							dispatch(
								updateScheduleFormActions.editOrder({
									data: createOrderData,
									timer,
									_id: order._id,
								}),
							)
						}}
						sx={{ color: '#1976d2', cursor: 'pointer' }}
					/>
					<IconButton
						onClick={() => deleteOrder(order._id)}
						sx={{
							p: 0,
							'&:hover': {
								backgroundColor: 'transparent',
								'& .delete-icon': {
									color: '#d32f2f',
								},
							},
						}}>
						<DeleteOutlineIcon
							className='delete-icon'
							sx={{
								color: '#ff0000',
								transition: 'color 0.2s',
							}}
						/>
					</IconButton>
					{showExpandIcon && <ExtendedIconButton isExpanded={isExpanded} onClick={() => toggleExpand(order._id)} />}
				</Box>
			</Paper>
		)
	}

	return (
		<UserLayout>
			<Box
				sx={{
					maxWidth: '992px', // Giới hạn width cho giống mobile
					margin: '0 auto', // Căn giữa
					bgcolor: '#f5f5f5',
					minHeight: '100vh',
				}}>
				{loadingSchedule ? (
					<Box display='flex' justifyContent='center' sx={{ marginTop: '120px' }}>
						<CircularProgress sx={{ color: mainGreen }} />
					</Box>
				) : (
					<>
						<Paper
							sx={{
								m: 2,
								p: 2,
								borderRadius: 4,
								bgcolor: 'white',
								boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
								overflow: 'hidden', // Ensures content doesn't overflow
							}}>
							<Typography variant='h6' sx={{ mb: 2 }}>
								Tổng quan đơn hàng
							</Typography>
							<Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
								<Table>
									<TableHead
										sx={{
											'& th': {
												backgroundColor: mainGreen,
												color: 'white',
												border: '1px solid rgba(255, 255, 255, 0.2)',
												padding: '8px',
												textAlign: 'center',
											},
										}}>
										<TableRow>
											<TableCell rowSpan={2}>Ngày</TableCell>
											<TableCell colSpan={2}>Ca sáng</TableCell>
											<TableCell colSpan={2}>Ca chiều</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>Số đơn</TableCell>
											<TableCell>Số kg</TableCell>
											<TableCell>Số đơn</TableCell>
											<TableCell>Số kg</TableCell>
										</TableRow>
									</TableHead>
									<TableBody
										sx={{
											'& td': {
												border: '1px solid rgba(224, 224, 224, 1)',
												padding: '8px',
												textAlign: 'center',
											},
										}}>
										<TableRow>
											<TableCell>{nearestScheduleOrders?.dayOne?.date}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayOne?.quantity?.morning}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayOne?.weight?.morning}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayOne?.quantity?.afternoon}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayOne?.weight?.afternoon}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>{nearestScheduleOrders?.dayTwo?.date}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayTwo?.quantity?.morning}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayTwo?.weight?.morning}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayTwo?.quantity?.afternoon}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayTwo?.weight?.afternoon}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>{nearestScheduleOrders?.dayThree?.date}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayThree?.quantity?.morning}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayThree?.weight?.morning}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayThree?.quantity?.afternoon}</TableCell>
											<TableCell>{nearestScheduleOrders?.dayThree?.weight?.afternoon}</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</Box>
						</Paper>
						<Box sx={[orderPanelStyles.container, { marginTop: 0 }]}>
							{scheduleOrders?.length === 0 && (
								<Card sx={{ padding: '10px' }}>
									<Typography sx={{ fontWeight: 'bold' }}>Chưa có đơn đặt trước</Typography>
								</Card>
							)}
							<Backdrop
								sx={{
									color: '#fff',
									zIndex: 999,
									left: mediaMd ? 300 : 0,
								}}
								open={isViewerOpen}
								onClick={closeImageViewer}>
								<img
									loading='lazy'
									src={currentImage}
									alt='attach'
									style={{
										maxHeight: '70%',
										maxWidth: '90%',
									}}
									onClick={(e) => {
										e.stopPropagation()
									}}
								/>
							</Backdrop>
							<Box sx={orderPanelStyles.ordersBox}>
								{scheduleOrders?.map((order, index) => (
									<React.Fragment key={index}>{renderOrderCard(order)}</React.Fragment>
								))}
								<div style={{ height: '100px' }} />
							</Box>
						</Box>
						<Box
							position='fixed'
							sx={{
								bottom: 0,
								left: mediaMd ? 300 : 0,
								right: 0,
							}}>
							{showUpdateScheduleForm && currentUser.permission?.includes('updateOrder') && (
								<UpdateScheduleOrderForm2
									storeId={storeId}
									getScheduleOrders={getScheduleOrders}
									open={showUpdateScheduleForm}
									handleShowForm={(isShow) => !isShow && dispatch(updateScheduleFormActions.hideUpdateScheduleForm())}
								/>
							)}
						</Box>
					</>
				)}
			</Box>
		</UserLayout>
	)
}
